import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { tenantQuery } from '../../../utils/firestoreHelpers';

const GMT3_OFFSET = 3;

export function getCurrentDate() {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const local = new Date(utc + 3600000 * GMT3_OFFSET);
  const y = local.getFullYear();
  const m = String(local.getMonth() + 1).padStart(2, '0');
  const d = String(local.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getDateRange() {
  const today = new Date();
  const dates = [today.toISOString().split('T')[0]];
  for (let i = 1; i <= 3; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    dates.push(d.toISOString().split('T')[0]);
  }
  return dates;
}

export async function fetchSupportingData(businessId) {
  const [customerSnap, employeeSnap, serviceSnap, packageSnap] = await Promise.all([
    getDocs(tenantQuery(collection(db, 'users'), businessId,
      where('role', '==', 'customer'),
      where('status', '==', 'active')
    )),
    getDocs(tenantQuery(collection(db, 'users'), businessId,
      where('role', '==', 'employee'),
      where('status', '==', 'active')
    )),
    getDocs(tenantQuery(collection(db, 'services'), businessId,
      where('status', '==', 'active')
    )),
    getDocs(tenantQuery(collection(db, 'packages'), businessId,
      where('status', '==', 'active')
    )),
  ]);
  return {
    customers: customerSnap.docs.map(d => ({ id: d.id, ...d.data() })),
    employees: employeeSnap.docs.map(d => ({ id: d.id, ...d.data() })),
    services:  serviceSnap.docs.map(d => ({ id: d.id, ...d.data() })),
    packages:  packageSnap.docs.map(d => ({ id: d.id, ...d.data() })),
  };
}

export async function fetchBillsData({ businessId, role, customers, employees, t }) {
  let billsQuery;

  if (role === 'admin') {
    const dateRange = getDateRange();
    billsQuery = tenantQuery(
      collection(db, 'bills'), businessId,
      where('status', '==', 'active'),
      where('date', 'in', dateRange)
    );
  } else {
    const today = getCurrentDate();
    billsQuery = tenantQuery(
      collection(db, 'bills'), businessId,
      where('status', '==', 'active'),
      where('date', '==', today)
    );
  }

  const snap = await getDocs(billsQuery);
  return snap.docs.map((d) => {
    const data = d.data();
    const customer = customers.find(c => c.id === data.customerId);
    const employee = employees.find(e => e.id === data.employeeId);
    return {
      id: d.id,
      ...data,
      customerName: customer ? `${customer.name} ${customer.surname}` : t('page.unknown'),
      employeeName: employee ? `${employee.name} ${employee.surname}` : t('page.unknown'),
      serviceNames: data.services?.map(s => s.name).join(', ') || t('page.unknown'),
    };
  });
}

export async function fetchCustomerActivePackages(businessId, customerId) {
  const snap = await getDocs(
    tenantQuery(
      collection(db, 'customer_packages'), businessId,
      where('customerId', '==', customerId),
      where('status', '==', 'active')
    )
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function softDeleteBill(billId) {
  await updateDoc(doc(db, 'bills', billId), {
    status: 'deleted',
    updatedAt: serverTimestamp(),
  });
}

// === Tracking Info ===
// trackingInfo belgelerine businessId eklenir; SaaS tenantRead() kuralının
// sorgu düzeyinde doğrulanabilmesi için businessId filtresi de kullanılır.

export async function getTrackingInfoByBillId(billId, businessId) {
  const constraints = [where('billId', '==', billId)];
  if (businessId) constraints.push(where('businessId', '==', businessId));
  const snap = await getDocs(query(collection(db, 'trackingInfo'), ...constraints));
  if (!snap.empty) {
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  }
  return null;
}

export async function saveTrackingInfo(trackingData) {
  const ref = await addDoc(collection(db, 'trackingInfo'), {
    ...trackingData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateTrackingInfo(id, trackingData) {
  await updateDoc(doc(db, 'trackingInfo', id), {
    ...trackingData,
    updatedAt: serverTimestamp(),
  });
}

// === Cost Service ===

export async function fetchCostsData({ businessId, role }) {
  let costsQuery;

  if (role === 'cashier') {
    const today = getCurrentDate();
    costsQuery = tenantQuery(
      collection(db, 'costs'), businessId,
      where('status', '==', 'active'),
      where('date', '==', today)
    );
  } else {
    costsQuery = tenantQuery(
      collection(db, 'costs'), businessId,
      where('status', '==', 'active')
    );
  }

  const snap = await getDocs(costsQuery);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function saveCost({ costData, costId }) {
  if (costId) {
    await updateDoc(doc(db, 'costs', costId), {
      ...costData,
      updatedAt: serverTimestamp(),
    });
  } else {
    await addDoc(collection(db, 'costs'), {
      ...costData,
      createdAt: serverTimestamp(),
    });
  }
}

export async function deleteCost(costId) {
  await deleteDoc(doc(db, 'costs', costId));
}
